import { useState, useEffect, useMemo } from 'react';
import useIsMobile from '../../shared/hooks/useIsMobile';
import { getPresetRange } from '../components/DateRangePicker';
import { getComparisonRange } from '../utils/reportingUtils';

const DATE_PARAMS = ['start_date', 'end_date'];

const filtersFromQuery = (query, urlFilterIds) => {
  const filters = {};
  query.forEach((value, key) => {
    if (DATE_PARAMS.includes(key)) return;
    const id = key.replace(/\[\]$/, '');
    if (!urlFilterIds.includes(id)) return;
    filters[id] = [...(filters[id] || []), value];
  });
  return filters;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const daysBefore = (date, days) => new Date(Date.parse(`${date}T00:00:00Z`) - days * DAY_MS).toISOString().slice(0, 10);

const boundedStart = (start, end, maxDays) => {
  const earliest = maxDays ? daysBefore(end, maxDays - 1) : start;
  const bounded = start > earliest ? start : earliest;
  return bounded > end ? end : bounded;
};

const rangeFromQuery = (query, defaultPreset, defaultStart, maxDefaultDays) => {
  const start = query.get('start_date');
  const end = query.get('end_date');
  if (start && end) return { start, end };

  const preset = getPresetRange(defaultPreset);
  return defaultStart ? { start: boundedStart(defaultStart, preset.end, maxDefaultDays), end: preset.end } : preset;
};

export default function useReportingPage(defaultPreset = 'this_year', { urlFilterIds = [], defaultStart, maxDefaultDays } = {}) {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  useEffect(() => { if (isMobile) setSidebarOpen(false); }, [isMobile]);

  const [query] = useState(() => new URLSearchParams(window.location.search));
  const [dateRange, setDateRange] = useState(() => rangeFromQuery(query, defaultPreset, defaultStart, maxDefaultDays));
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(() => filtersFromQuery(query, urlFilterIds));

  const comparison = useMemo(() => getComparisonRange(dateRange), [dateRange]);

  const handleFilterChange = (newFilters) => setActiveFilters(newFilters);

  const handleRemoveFilter = (filterId) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterId];
    setActiveFilters(newFilters);
  };

  const handleClearAllFilters = () => setActiveFilters({});

  return {
    isMobile,
    sidebarOpen,
    setSidebarOpen,
    dateRange,
    setDateRange,
    filterSidebarOpen,
    setFilterSidebarOpen,
    activeFilters,
    setActiveFilters,
    comparison,
    handleFilterChange,
    handleRemoveFilter,
    handleClearAllFilters,
  };
}
